/**
 * Sprint 16 — Unified Talk HPB Panel.
 *
 * Replaces Sprint 15's separate Talk Signaling / TURN / External Hosts
 * panels with ONE pool that bundles BOTH signaling + TURN. Each pool
 * has TWO kinds of members: role="signaling" + role="turn". The
 * operator scales them independently.
 *
 * Each pool member can be:
 *   - mode="vm"  → references a CM-managed talk-signaling or coturn VM
 *   - mode="ssh" → inline SSH credentials, CM deploys the binary via SSH
 */
import React, { useState } from "react";
import useSWR from "swr";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Spinner, Empty, StatusBadge } from "@/components/Atoms";
import { Plus, Trash2, KeyRound, Mic, Wifi, Wrench, Server, Video } from "lucide-react";
// We re-use the Collabora-style "Add VM" flow from the main pool panel.
// AddServiceVmFlow handles: auto-create pool if missing → provision VM →
// service_vms backend auto-registers it as a pool member.
import { AddServiceVmFlow } from "@/pages/InfrastructurePoolsExtra";

const fetcher = (u) => api.get(u).then((r) => r.data);

function Field({ label, v, on, testid, type = "text", placeholder, hint }) {
    return (
        <label className="block">
            <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">{label}</span>
            <input
                type={type}
                value={v ?? ""}
                onChange={(e) => on(e.target.value)}
                data-testid={testid}
                placeholder={placeholder}
                className="cm-input w-full"
            />
            {hint && <span className="text-[11px] text-zinc-500 block mt-0.5">{hint}</span>}
        </label>
    );
}

function TextArea({ label, v, on, testid, placeholder, rows = 6 }) {
    return (
        <label className="block">
            <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">{label}</span>
            <textarea
                value={v ?? ""}
                onChange={(e) => on(e.target.value)}
                data-testid={testid}
                placeholder={placeholder}
                rows={rows}
                className="cm-input w-full font-mono text-[11px]"
            />
        </label>
    );
}


export function TalkHpbPanel() {
    const { data: pools, mutate: mutatePools } = useSWR(
        "/api/admin/infra/talk-hpb-pools", fetcher, { refreshInterval: 5000 });
    const { data: sigVms } = useSWR(
        "/api/admin/infra/service-vms?kind=talk-signaling", fetcher,
        { refreshInterval: 5000 });
    const { data: turnVms } = useSWR(
        "/api/admin/infra/service-vms?kind=coturn", fetcher,
        { refreshInterval: 5000 });
    const { data: recVms } = useSWR(
        "/api/admin/infra/service-vms?kind=talk-recording", fetcher,
        { refreshInterval: 5000 });
    const [showAddPool, setShowAddPool] = useState(false);
    const [showAddMember, setShowAddMember] = useState(null);   // {poolId, role}
    const [showProvision, setShowProvision] = useState(null);   // {poolId, kind: "talk-signaling"|"coturn"|"talk-recording"}
    const [createdSecret, setCreatedSecret] = useState(null);
    const [err, setErr] = useState(null);
    const [turnTest, setTurnTest] = useState(null);   // {poolId, loading, data}

    const onTestTurn = async (poolId) => {
        setTurnTest({ poolId, loading: true, data: null });
        try {
            const r = await api.post(`/api/admin/infra/talk-hpb-pools/${poolId}/test-turn`);
            setTurnTest({ poolId, loading: false, data: r.data });
            if (r.data.ok) toast.success(`TURN ok — ${r.data.summary}`);
            else toast.error(`TURN-Test: Problem — ${r.data.summary}`);
        } catch (e) {
            setTurnTest({ poolId, loading: false, data: null });
            toast.error("TURN test failed: " + (e?.response?.data?.detail || e.message));
        }
    };

    if (!pools && !sigVms && !turnVms) return <Spinner />;

    const onDeletePool = async (id) => {
        if (!confirm("Destroy this Talk HPB pool?")) return;
        try { await api.delete(`/api/admin/infra/talk-hpb-pools/${id}`); }
        catch (e) { setErr(e?.response?.data?.detail || String(e)); }
        mutatePools();
    };
    const onDeleteMember = async (poolId, memberId) => {
        if (!confirm("Remove this member?")) return;
        try { await api.delete(`/api/admin/infra/talk-hpb-pools/${poolId}/members/${memberId}`); }
        catch (e) { setErr(e?.response?.data?.detail || String(e)); }
        mutatePools();
    };
    const onDeploySsh = async (poolId, memberId) => {
        if (!confirm("Run cloud-init on this SSH member? Takes up to 15 minutes (apt + curl + binary download).")) return;
        try {
            const r = await api.post(
                `/api/admin/infra/talk-hpb-pools/${poolId}/members/${memberId}/deploy-ssh`);
            const tail = r.data.ok
                ? `OK · exit_code=${r.data.exit_code} · ${r.data.duration_ms}ms`
                : `FAIL · ${r.data.error || r.data.stderr_tail?.slice(0,300) || "see logs"}`;
            alert(tail);
        } catch (e) { setErr(e?.response?.data?.detail || String(e)); }
        mutatePools();
    };

    return (
        <div className="space-y-4">
            <div className="cm-card p-3 text-xs text-zinc-400 bg-zinc-950 border border-zinc-800">
                <strong className="text-zinc-200">Talk HPB — High Performance Backend for calls</strong>
                <ul className="mt-1 space-y-0.5 ml-3 list-disc">
                    <li>A pool bundles the <strong>signaling SFU</strong> (nextcloud-spreed-signaling) + <strong>TURN/STUN</strong> (coTURN).</li>
                    <li>Every pool needs at least <strong>1× signaling member + 1× TURN member</strong> active to be usable.</li>
                    <li><strong>VM mode:</strong> CM provisioniert eine Service-VM auf einem Control-Plane Node.</li>
                    <li><strong>SSH mode:</strong> SSH-Credentials direkt eintragen → CM deployt das Binary auf einem externen Bare-Metal Server.</li>
                    <li>Enabling it on a cloud installs + configures NC Talk completely (spreed + signaling + turn) in <strong>one click</strong>.</li>
                </ul>
            </div>

            {createdSecret && (
                <div className="cm-card p-3 border border-amber-700 bg-amber-950/40">
                    <div className="text-xs text-amber-200 mb-1 flex items-center gap-1">
                        <KeyRound size={12} /> One-time secrets for pool <strong>{createdSecret.pool}</strong>
                    </div>
                    <div className="text-[11px] text-zinc-400 mb-0.5">TURN static-auth-secret</div>
                    <div className="font-mono text-[11px] break-all p-2 bg-zinc-950 border border-zinc-800" data-testid="thpb-pool-created-secret">
                        {createdSecret.secret}
                    </div>
                    {createdSecret.recordingSecret && (
                        <>
                            <div className="text-[11px] text-zinc-400 mb-0.5 mt-2">Recording shared secret</div>
                            <div className="font-mono text-[11px] break-all p-2 bg-zinc-950 border border-zinc-800" data-testid="thpb-pool-created-recording-secret">
                                {createdSecret.recordingSecret}
                            </div>
                        </>
                    )}
                    <div className="text-[11px] text-zinc-400 mt-1">
                        Copy these — they&apos;re only shown once. Needed if you deploy coTURN / a recording backend manually on an external host.
                    </div>
                    <button onClick={() => setCreatedSecret(null)} className="cm-btn-ghost text-[11px] px-2 py-1 mt-2">Dismiss</button>
                </div>
            )}

            <div className="flex justify-between items-center mb-2">
                <div className="cm-label">Talk HPB Pools</div>
                <div className="flex gap-2">
                    <button data-testid="thpb-dns-recheck-btn"
                             onClick={async () => {
                                 try {
                                     const r = await api.post("/api/admin/infra/talk-hpb-pools/dns-recheck");
                                     await mutatePools();
                                     const n = r?.data?.checked ?? 0;
                                     toast.success(`DNS rechecked — ${n} hostname(s) resolved against the LB / VM target IPs.`);
                                 } catch (e) {
                                     toast.error("DNS recheck failed: " + (e?.response?.data?.detail || e.message));
                                 }
                             }}
                             className="cm-btn-ghost text-xs px-3 py-1.5 flex items-center gap-1 border border-zinc-700">
                        Recheck DNS
                    </button>
                    <button data-testid="thpb-pool-add-btn" onClick={() => setShowAddPool(true)}
                             className="cm-btn-primary text-xs px-3 py-1.5 flex items-center gap-1">
                        <Plus size={12} /> Create Pool
                    </button>
                </div>
            </div>
            {showAddPool && (
                <AddTalkHpbPoolForm
                    onClose={() => setShowAddPool(false)}
                    onCreated={(d) => {
                        setCreatedSecret({ pool: d.name, secret: d.turn_static_auth_secret, recordingSecret: d.recording_secret });
                        mutatePools();
                        setShowAddPool(false);
                    }}
                />
            )}
            {(pools || []).length === 0 && !showAddPool && (
                <Empty title="No Talk HPB pools" hint="Calls > 6 participants need a working signaling SFU." />
            )}

            <div className="space-y-3">
                {(pools || []).map(p => {
                    const sigMembers = (p.members || []).filter(m => m.role === "signaling");
                    const turnMembers = (p.members || []).filter(m => m.role === "turn");
                    const recMembers = (p.members || []).filter(m => m.role === "recording");
                    const usable = p.signaling_count > 0 && p.turn_count > 0;
                    return (
                        <div key={p.id} className="cm-card p-3" data-testid={`thpb-pool-${p.id}`}>
                            <div className="flex justify-between items-center">
                                <div>
                                    <span className="font-mono text-sm">{p.name}</span>
                                    <span className="ml-2 cm-badge cm-badge-info text-[10px]">{p.type}</span>
                                    <span className={`ml-2 cm-badge text-[10px] ${usable ? "cm-badge-success" : "cm-badge-warn"}`}>
                                        {usable ? "ready" : "incomplete"}
                                    </span>
                                    <div className="text-[11px] text-zinc-500 mt-0.5">
                                        Tenants bound: {p.current_clouds || 0}/{p.max_clouds} ·{" "}
                                        Signaling: {p.signaling_count} ·{" "}
                                        TURN: {p.turn_count} ·{" "}
                                        Recording: {p.recording_count || 0}
                                    </div>
                                    {p.signaling_domain && (
                                        <div className="text-[11px] text-[#4d9fff] mt-0.5 font-mono" data-testid={`thpb-pool-domain-${p.id}`}>
                                            wss://{p.signaling_domain} (Caddy LB route)
                                            <DnsBadge host={p.dns_host || p.signaling_domain} ok={p.dns_ok}
                                                       expected={p.dns_expected_ips} resolved={p.dns_resolved_ips} />
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-1">
                                    <button data-testid={`thpb-pool-test-turn-${p.id}`}
                                             onClick={() => onTestTurn(p.id)}
                                             disabled={turnTest?.poolId === p.id && turnTest?.loading}
                                             title="Live-Test: STUN-Binding + authentifizierter TURN-Allocate gegen alle coTURN-Member dieses Pools"
                                             className="cm-btn-ghost text-[11px] px-2 py-1 text-sky-300 flex items-center gap-1 border border-sky-800 disabled:opacity-50">
                                        <Wifi size={10} /> {turnTest?.poolId === p.id && turnTest?.loading ? "Teste…" : "TURN testen"}
                                    </button>
                                    <button data-testid={`thpb-pool-repush-${p.id}`}
                                             onClick={async () => {
                                                 if (!confirm("Signaling server.conf auf allen VMs dieses Pools neu schreiben (allowall + secret) UND Talk-Config an alle gebundenen Clouds neu pushen?")) return;
                                                 try {
                                                     const r = await api.post(`/api/admin/infra/talk-hpb-pools/${p.id}/repush`);
                                                     const sig = (r.data.signaling?.members || []).filter(m => m.ok === true).length;
                                                     toast.success(`Re-push: ${r.data.bumped}/${r.data.clouds} Clouds · ${sig} Signaling-VM(s) neu konfiguriert.`);
                                                 } catch (e) {
                                                     toast.error("Re-push failed: " + (e?.response?.data?.detail || e.message));
                                                 }
                                                 mutatePools();
                                             }}
                                             title="Signaling-Config (allowall + secret) auf alle Pool-VMs schreiben + Talk-Config an alle gebundenen Clouds neu pushen"
                                             className="cm-btn-ghost text-[11px] px-2 py-1 text-amber-300 flex items-center gap-1 border border-amber-800">
                                        <Wrench size={10} /> Re-push
                                    </button>
                                    <button data-testid={`thpb-pool-delete-${p.id}`}
                                             onClick={() => onDeletePool(p.id)}
                                             className="cm-btn-ghost text-[11px] px-2 py-1 text-red-300 flex items-center gap-1">
                                        <Trash2 size={10} /> Delete
                                    </button>
                                </div>
                            </div>

                            {/* TURN test results */}
                            {turnTest?.poolId === p.id && turnTest?.data && (
                                <div className="mt-2 p-2 rounded bg-zinc-950 border border-zinc-800" data-testid={`thpb-pool-turn-result-${p.id}`}>
                                    <div className={`text-xs font-medium flex items-center gap-1 ${turnTest.data.ok ? "text-emerald-300" : "text-red-300"}`}>
                                        <Wifi size={11} /> TURN-Test: {turnTest.data.ok ? "OK" : "Problem"} — {turnTest.data.summary}
                                    </div>
                                    {!turnTest.data.has_secret && (
                                        <div className="text-[11px] text-amber-300 mt-1">⚠ No static-auth-secret in the pool — only STUN tested (Allocate skipped).</div>
                                    )}
                                    <div className="mt-1 space-y-0.5">
                                        {(turnTest.data.results || []).map((r, i) => (
                                            <div key={i} className="text-[11px] font-mono flex items-start gap-2" data-testid={`thpb-turn-uri-${p.id}-${i}`}>
                                                <span className={r.stun && (r.turn === null || r.turn) ? "text-emerald-400" : "text-red-400"}>
                                                    {r.stun && (r.turn === null || r.turn) ? "✓" : "✗"}
                                                </span>
                                                <span className="text-zinc-300 break-all">{r.uri}</span>
                                                <span className="text-zinc-500">— {r.detail || (r.stun ? "STUN ok" : "kein STUN")}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Signaling sub-section */}
                            <div className="mt-3 border-t border-zinc-800 pt-2">
                                <div className="flex justify-between items-center mb-1">
                                    <div className="text-xs text-zinc-300 flex items-center gap-1">
                                        <Mic size={11} /> Signaling Members ({sigMembers.length})
                                    </div>
                                    <div className="flex gap-1">
                                        <button data-testid={`thpb-pool-provision-sig-${p.id}`}
                                                 onClick={() => setShowProvision({ poolId: p.id, kind: "talk-signaling" })}
                                                 className="cm-btn-ghost text-[11px] px-2 py-1 flex items-center gap-1 border border-zinc-700">
                                            <Server size={10} /> Provision Signaling VM
                                        </button>
                                        <button data-testid={`thpb-pool-add-sig-${p.id}`}
                                                 onClick={() => setShowAddMember({ poolId: p.id, role: "signaling" })}
                                                 className="cm-btn-ghost text-[11px] px-2 py-1 flex items-center gap-1 border border-zinc-700">
                                            <Plus size={10} /> Add (External SSH)
                                        </button>
                                    </div>
                                </div>
                                {showProvision?.poolId === p.id && showProvision?.kind === "talk-signaling" && (
                                    <div className="my-2">
                                        <AddServiceVmFlow kind="talk-signaling" presetPoolId={p.id}
                                                           onClose={() => { setShowProvision(null); mutatePools(); }} />
                                    </div>
                                )}
                                {showAddMember?.poolId === p.id && showAddMember?.role === "signaling" && (
                                    <AddTalkHpbMemberForm
                                        poolId={p.id} role="signaling"
                                        vms={sigVms || []}
                                        onClose={() => { setShowAddMember(null); mutatePools(); }}
                                    />
                                )}
                                <MemberList members={sigMembers} role="signaling"
                                              onDelete={(mid) => onDeleteMember(p.id, mid)}
                                              onDeploy={(mid) => onDeploySsh(p.id, mid)} />
                            </div>

                            {/* TURN sub-section */}
                            <div className="mt-3 border-t border-zinc-800 pt-2">
                                <div className="flex justify-between items-center mb-1">
                                    <div className="text-xs text-zinc-300 flex items-center gap-1">
                                        <Wifi size={11} /> TURN Members ({turnMembers.length})
                                    </div>
                                    <div className="flex gap-1">
                                        <button data-testid={`thpb-pool-provision-turn-${p.id}`}
                                                 onClick={() => setShowProvision({ poolId: p.id, kind: "coturn" })}
                                                 className="cm-btn-ghost text-[11px] px-2 py-1 flex items-center gap-1 border border-zinc-700">
                                            <Server size={10} /> Provision TURN VM
                                        </button>
                                        <button data-testid={`thpb-pool-add-turn-${p.id}`}
                                                 onClick={() => setShowAddMember({ poolId: p.id, role: "turn" })}
                                                 className="cm-btn-ghost text-[11px] px-2 py-1 flex items-center gap-1 border border-zinc-700">
                                            <Plus size={10} /> Add (External SSH)
                                        </button>
                                    </div>
                                </div>
                                {showProvision?.poolId === p.id && showProvision?.kind === "coturn" && (
                                    <div className="my-2">
                                        <AddServiceVmFlow kind="coturn" presetPoolId={p.id}
                                                           onClose={() => { setShowProvision(null); mutatePools(); }} />
                                    </div>
                                )}
                                {showAddMember?.poolId === p.id && showAddMember?.role === "turn" && (
                                    <AddTalkHpbMemberForm
                                        poolId={p.id} role="turn"
                                        vms={turnVms || []}
                                        onClose={() => { setShowAddMember(null); mutatePools(); }}
                                    />
                                )}
                                <MemberList members={turnMembers} role="turn"
                                              onDelete={(mid) => onDeleteMember(p.id, mid)}
                                              onDeploy={(mid) => onDeploySsh(p.id, mid)} />
                            </div>

                            {/* Recording sub-section */}
                            <div className="mt-3 border-t border-zinc-800 pt-2">
                                <div className="flex justify-between items-center mb-1">
                                    <div className="text-xs text-zinc-300 flex items-center gap-1">
                                        <Video size={11} /> Recording Members ({recMembers.length})
                                    </div>
                                    <div className="flex gap-1">
                                        <button data-testid={`thpb-pool-provision-rec-${p.id}`}
                                                 onClick={() => setShowProvision({ poolId: p.id, kind: "talk-recording" })}
                                                 className="cm-btn-ghost text-[11px] px-2 py-1 flex items-center gap-1 border border-zinc-700">
                                            <Server size={10} /> Provision Recording VM
                                        </button>
                                        <button data-testid={`thpb-pool-add-rec-${p.id}`}
                                                 onClick={() => setShowAddMember({ poolId: p.id, role: "recording" })}
                                                 className="cm-btn-ghost text-[11px] px-2 py-1 flex items-center gap-1 border border-zinc-700">
                                            <Plus size={10} /> Add (External SSH)
                                        </button>
                                    </div>
                                </div>
                                <div className="text-[11px] text-zinc-500 mb-1">
                                    Optional — records Talk calls (chromium + ffmpeg). Pool stays usable without it.
                                </div>
                                {showProvision?.poolId === p.id && showProvision?.kind === "talk-recording" && (
                                    <div className="my-2">
                                        <AddServiceVmFlow kind="talk-recording" presetPoolId={p.id}
                                                           onClose={() => { setShowProvision(null); mutatePools(); }} />
                                    </div>
                                )}
                                {showAddMember?.poolId === p.id && showAddMember?.role === "recording" && (
                                    <AddTalkHpbMemberForm
                                        poolId={p.id} role="recording"
                                        vms={recVms || []}
                                        onClose={() => { setShowAddMember(null); mutatePools(); }}
                                    />
                                )}
                                <MemberList members={recMembers} role="recording"
                                              onDelete={(mid) => onDeleteMember(p.id, mid)}
                                              onDeploy={(mid) => onDeploySsh(p.id, mid)} />
                            </div>
                        </div>
                    );
                })}
            </div>
            {err && <div className="text-red-400 text-xs font-mono break-all" data-testid="thpb-err">{err}</div>}
        </div>
    );
}


function DnsBadge({ host, ok, expected, resolved }) {
    if (!host) return null;
    const exp = (expected || []).filter(Boolean);
    const res = (resolved || []).filter(Boolean);
    if (ok) {
        return <span className="ml-2 cm-badge cm-badge-success text-[10px]"
                      title={`resolves to ${res.join(", ")}`}
                      data-testid={`dns-ok-${host}`}>DNS OK</span>;
    }
    // DNS already resolves, but CM doesn't know the expected target (no Caddy
    // LB public IP recorded) → can't verify. Tell the operator exactly that.
    if (exp.length === 0 && res.length > 0) {
        return (
            <span className="ml-2 cm-badge cm-badge-warn text-[10px]"
                   title={`Resolves to ${res.join(", ")}. CM can't verify because no Caddy LB public IP is recorded — set it on the LB so CM knows the expected target.`}
                   data-testid={`dns-unknown-target-${host}`}>
                resolves → {res.join(", ")} · LB target unknown
            </span>
        );
    }
    const target = exp.join(", ")
        || (res.length ? "your Caddy LB's public IP" : "(no Caddy LB / target IP yet)");
    return (
        <span className="ml-2 cm-badge cm-badge-warn text-[10px]"
               title={`Set an A-record: ${host} → ${target}`}
               data-testid={`dns-pending-${host}`}>
            DNS pending — set A: {host} → {target}
        </span>
    );
}


function MemberList({ members, role, onDelete, onDeploy }) {
    if (!members.length) return <div className="text-zinc-500 text-[11px] italic ml-2">no members yet</div>;
    return (
        <div className="space-y-1 ml-2">
            {members.map(m => (
                <div key={m.id} className="bg-zinc-950 border border-zinc-800 p-2 text-xs flex justify-between items-center" data-testid={`thpb-member-${m.id}`}>
                    <div>
                        <span className="font-mono">{m.host}:{m.port}</span>
                        <span className="ml-2 cm-badge cm-badge-neutral text-[10px]">{m.mode}</span>
                        <StatusBadge status={m.status} />
                        {role === "turn" && (
                            <span className="ml-2 text-[11px] text-zinc-500">
                                UDP:{m.udp_port || m.port} TCP:{m.tcp_port || m.port}{m.use_tls && ` TLS:${m.tls_port}`}
                            </span>
                        )}
                        {role === "turn" && m.turn_domain && (
                            <div className="text-[11px] text-[#4d9fff] mt-0.5 font-mono">
                                {m.turn_domain}
                                <DnsBadge host={m.turn_domain} ok={m.dns_ok}
                                           expected={m.dns_expected_ips} resolved={m.dns_resolved_ips} />
                            </div>
                        )}
                        {m.last_deploy_error && (
                            <div className="text-[11px] text-red-300 mt-0.5 break-all">deploy err: {m.last_deploy_error}</div>
                        )}
                    </div>
                    <div className="flex gap-1">
                        {m.mode === "ssh" && (
                            <button data-testid={`thpb-member-deploy-${m.id}`}
                                     onClick={() => onDeploy(m.id)}
                                     title="Run cloud-init over SSH (idempotent)"
                                     className="cm-btn-ghost text-[11px] px-2 py-1 border border-zinc-700 flex items-center gap-1">
                                <Wrench size={10} /> Deploy
                            </button>
                        )}
                        <button data-testid={`thpb-member-delete-${m.id}`}
                                 onClick={() => onDelete(m.id)}
                                 className="cm-btn-ghost text-[11px] px-2 py-1 text-red-300">
                            <Trash2 size={10} />
                        </button>
                    </div>
                </div>
            ))}
        </div>
    );
}


function AddTalkHpbPoolForm({ onClose, onCreated }) {
    const [name, setName] = useState("");
    const [type, setType] = useState("cluster");
    const [signalingDomain, setSignalingDomain] = useState("");
    const [secret, setSecret] = useState("");
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);
    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr(null);
        try {
            const body = { name, type };
            if (signalingDomain) body.signaling_domain = signalingDomain.trim();
            if (secret) body.turn_static_auth_secret = secret;
            const r = await api.post("/api/admin/infra/talk-hpb-pools", body);
            onCreated(r.data);
        } catch (e) { setErr(e?.response?.data?.detail || String(e)); }
        finally { setBusy(false); }
    };
    return (
        <form onSubmit={submit} className="cm-card p-3 space-y-2 mb-3 border border-[#CCFF00]" data-testid="thpb-pool-form">
            <div className="cm-h3 text-sm">Create Talk HPB Pool</div>
            <Field label="Name" v={name} on={setName} testid="thpb-pool-name" placeholder="default-talk-hpb" />
            <label>
                <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">Type</span>
                <select value={type} onChange={(e) => setType(e.target.value)} className="cm-input w-full" data-testid="thpb-pool-type">
                    <option value="cluster">cluster (many members)</option>
                    <option value="standalone">standalone (1 of each role)</option>
                </select>
            </label>
            <Field label="Signaling domain (optional)" v={signalingDomain} on={setSignalingDomain}
                    testid="thpb-pool-signaling-domain"
                    placeholder="auto: talk-sig-<id>.oncloud.zone"
                    hint="Leave blank → auto-generated under oncloud.zone. CM creates the Caddy LB route + shows the A-record (host → LB IP) to set." />
            <Field label="TURN static-auth-secret (optional, auto-generated)" v={secret} on={setSecret}
                    testid="thpb-pool-secret-override"
                    hint="every TURN member shares this; revealed ONCE on create" />
            {err && <div className="text-red-400 text-xs font-mono break-all">{err}</div>}
            <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={onClose} className="cm-btn-ghost text-xs px-3 py-1.5">Cancel</button>
                <button type="submit" disabled={busy || !name} className="cm-btn-primary text-xs px-3 py-1.5 disabled:opacity-50" data-testid="thpb-pool-create-submit">
                    {busy ? "…" : "Create"}
                </button>
            </div>
        </form>
    );
}


function AddTalkHpbMemberForm({ poolId, role, vms, onClose }) {
    const isSignaling = role === "signaling";
    const isTurn = role === "turn";
    const isRecording = role === "recording";
    const roleLabel = isSignaling ? "Signaling" : isTurn ? "TURN" : "Recording";
    const vmKind = isSignaling ? "talk-signaling" : isTurn ? "coturn" : "talk-recording";
    const defaultPort = isSignaling ? 8080 : isTurn ? 3478 : 1234;
    const [mode, setMode] = useState("vm");
    const [serviceVmId, setServiceVmId] = useState(vms?.[0]?.id || "");
    const [host, setHost] = useState("");
    const [port, setPort] = useState(defaultPort);
    const [publicUrl, setPublicUrl] = useState("");
    const [useTls, setUseTls] = useState(!isRecording);
    // signaling-only:
    const [sharedSecret, setSharedSecret] = useState("");
    // turn-only:
    const [udpPort, setUdpPort] = useState("");
    const [tcpPort, setTcpPort] = useState("");
    const [tlsPort, setTlsPort] = useState(5349);
    // ssh-only:
    const [sshHost, setSshHost] = useState("");
    const [sshPort, setSshPort] = useState(22);
    const [sshUser, setSshUser] = useState("root");
    const [sshKey, setSshKey] = useState("");
    const [fingerprint, setFingerprint] = useState("");

    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true); setErr(null);
        try {
            const body = {
                role,
                mode,
                host: host || (mode === "vm" ? (vms.find(v => v.id === serviceVmId)?.ip_internal || vms.find(v => v.id === serviceVmId)?.ip_external) : sshHost),
                port: parseInt(port, 10) || defaultPort,
                public_url: publicUrl || null,
                use_tls: useTls,
            };
            if (mode === "vm") {
                body.service_vm_id = serviceVmId;
            } else {
                body.ssh_host = sshHost;
                body.ssh_port = parseInt(sshPort, 10) || 22;
                body.ssh_user = sshUser;
                body.ssh_private_key = sshKey;
                body.known_hosts_fingerprint = fingerprint || null;
            }
            if (isSignaling) {
                body.signaling_shared_secret = sharedSecret || null;
            } else if (isTurn) {
                body.udp_port = parseInt(udpPort, 10) || null;
                body.tcp_port = parseInt(tcpPort, 10) || null;
                body.tls_port = parseInt(tlsPort, 10) || 5349;
            }
            await api.post(`/api/admin/infra/talk-hpb-pools/${poolId}/members`, body);
            onClose();
        } catch (e) { setErr(e?.response?.data?.detail || String(e)); }
        finally { setBusy(false); }
    };

    return (
        <form onSubmit={submit} className="cm-card p-3 space-y-2 my-2 border border-[#CCFF00]" data-testid={`thpb-member-form-${role}`}>
            <div className="cm-h3 text-sm">
                Add {roleLabel} Member
            </div>
            <div className="flex gap-2">
                <button type="button" onClick={() => setMode("vm")}
                         className={`text-xs px-3 py-1.5 border ${mode === "vm" ? "border-[#CCFF00] text-[#CCFF00] bg-[#10160a]" : "border-zinc-700 text-zinc-400"}`}
                         data-testid="thpb-member-mode-vm">VM (Control Plane)</button>
                <button type="button" onClick={() => setMode("ssh")}
                         className={`text-xs px-3 py-1.5 border ${mode === "ssh" ? "border-[#CCFF00] text-[#CCFF00] bg-[#10160a]" : "border-zinc-700 text-zinc-400"}`}
                         data-testid="thpb-member-mode-ssh">External SSH (Bare Metal)</button>
            </div>

            {mode === "vm" ? (
                <label>
                    <span className="block text-xs text-zinc-400 uppercase tracking-wider mb-1">
                        {vmKind} Service-VM
                    </span>
                    <select value={serviceVmId} onChange={(e) => setServiceVmId(e.target.value)} className="cm-input w-full" data-testid="thpb-member-vm-select">
                        {vms.length === 0 && <option value="">— provision a {vmKind} VM first —</option>}
                        {vms.map(v => <option key={v.id} value={v.id}>{v.hostname || v.id.slice(0,12)} — {v.ip_external || v.ip_internal}</option>)}
                    </select>
                </label>
            ) : (
                <div className="space-y-2 border border-zinc-800 p-2">
                    <div className="text-[11px] text-zinc-400 uppercase tracking-wider">SSH Credentials</div>
                    <div className="grid grid-cols-3 gap-2">
                        <div className="col-span-2"><Field label="SSH Host" v={sshHost} on={setSshHost} testid="thpb-member-sshhost" placeholder="talk.example.com" /></div>
                        <Field label="Port" type="number" v={sshPort} on={setSshPort} testid="thpb-member-sshport" />
                    </div>
                    <Field label="SSH User" v={sshUser} on={setSshUser} testid="thpb-member-sshuser" />
                    <TextArea label="SSH Private Key (OpenSSH PEM)" v={sshKey} on={setSshKey} testid="thpb-member-sshkey" rows={6}
                                placeholder="-----BEGIN OPENSSH PRIVATE KEY-----..." />
                    <Field label="Fingerprint (optional, SHA256:...)" v={fingerprint} on={setFingerprint} testid="thpb-member-fingerprint" />
                </div>
            )}

            <Field label="Public host/FQDN (what NC contacts; overrides VM IP)" v={host} on={setHost} testid="thpb-member-host" />
            <div className="grid grid-cols-3 gap-2">
                <Field label="Port" type="number" v={port} on={setPort} testid="thpb-member-port" />
                {isTurn && (
                    <>
                        <Field label="TLS Port" type="number" v={tlsPort} on={setTlsPort} testid="thpb-member-tls-port" />
                        <label className="flex items-center gap-1 text-xs text-zinc-300 mt-5">
                            <input type="checkbox" checked={useTls} onChange={(e) => setUseTls(e.target.checked)} data-testid="thpb-member-usetls" /> TLS
                        </label>
                    </>
                )}
                {!isTurn && (
                    <label className="flex items-center gap-1 text-xs text-zinc-300 mt-5">
                        <input type="checkbox" checked={useTls} onChange={(e) => setUseTls(e.target.checked)} data-testid="thpb-member-usetls" /> TLS
                    </label>
                )}
            </div>
            <Field label="Public URL (overrides scheme://host:port)" v={publicUrl} on={setPublicUrl} testid="thpb-member-publicurl"
                    placeholder={isSignaling ? "https://sig.example.com" : isTurn ? "turn.example.com" : "http://rec.example.com:1234"} />

            {isSignaling && (
                <Field label="Shared Secret (auto-pulled from VM creds if blank)"
                        v={sharedSecret} on={setSharedSecret} testid="thpb-member-sigsecret"
                        hint="JWT HMAC for backend → signaling traffic" />
            )}
            {isTurn && (
                <div className="grid grid-cols-2 gap-2">
                    <Field label="UDP Port (default = Port)" type="number" v={udpPort} on={setUdpPort} testid="thpb-member-udpport" />
                    <Field label="TCP Port (default = Port)" type="number" v={tcpPort} on={setTcpPort} testid="thpb-member-tcpport" />
                </div>
            )}

            {err && <div className="text-red-400 text-xs font-mono break-all">{err}</div>}
            <div className="flex justify-end gap-2 pt-1">
                <button type="button" onClick={onClose} className="cm-btn-ghost text-xs px-3 py-1.5">Cancel</button>
                <button type="submit" disabled={busy}
                         className="cm-btn-primary text-xs px-3 py-1.5 disabled:opacity-50"
                         data-testid={`thpb-member-submit-${role}`}>
                    {busy ? "…" : `Add ${roleLabel}`}
                </button>
            </div>
        </form>
    );
}
